import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { CATEGORIES, SITE, WHATSAPP_URL } from "@/constants/site";

const EMPTY = { name: "", phone: "", email: "", service: "", message: "" };

export const QuoteModal = ({ open, preset, onClose }) => {
  const [form, setForm] = useState(EMPTY);
  const [listingTitle, setListingTitle] = useState(null);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (open) {
      setForm((f) => ({ ...EMPTY, name: f.name, phone: f.phone, email: f.email, service: preset?.service || "" }));
      setListingTitle(preset?.listing || null);
    }
  }, [open, preset]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/quotes", {
        ...form,
        service: form.service || "autre",
        listing_title: listingTitle,
      });
      toast.success("Demande envoyée ! Nous vous recontactons rapidement.");
      onClose();
      setForm(EMPTY);
    } catch {
      toast.error("Envoi impossible. Réessayez ou contactez-nous sur WhatsApp.");
    } finally {
      setSending(false);
    }
  };

  const inputCls =
    "w-full bg-[#0D1117] border border-[#21262D] rounded-xl px-4 py-3 text-sm text-[#F0F6FC] placeholder:text-[#8B949E]/60 focus:outline-none focus:border-[#F2541B] transition-colors duration-300";

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[100] bg-[#0D1117]/85 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-6"
          onClick={onClose}
          data-testid="quote-modal-overlay"
        >
          <motion.div
            initial={{ y: 60, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 60, opacity: 0 }}
            transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
            onClick={(e) => e.stopPropagation()}
            data-testid="quote-request-modal"
            className="w-full sm:max-w-lg bg-[#161B22] border border-[#21262D] rounded-t-3xl sm:rounded-3xl p-7 sm:p-9 max-h-[90vh] overflow-y-auto"
          >
            <div className="flex items-start justify-between mb-2">
              <div>
                <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#E5A93C] mb-2">Réponse sous 24h</p>
                <h3 className="font-display font-bold uppercase tracking-tight text-2xl text-[#F0F6FC]">
                  Demande de devis
                </h3>
              </div>
              <button
                onClick={onClose}
                data-testid="quote-close-button"
                className="text-[#8B949E] hover:text-[#F0F6FC] transition-colors"
                aria-label="Fermer"
              >
                <X size={22} />
              </button>
            </div>

            {listingTitle && (
              <p className="font-mono text-xs text-[#F2541B] border border-[#F2541B]/30 rounded-lg px-3 py-2 mb-5" data-testid="quote-preset-listing">
                Concernant : {listingTitle}
              </p>
            )}

            <form onSubmit={submit} className="flex flex-col gap-4 mt-4" data-testid="quote-form">
              <input required placeholder="Votre nom *" value={form.name} onChange={set("name")} className={inputCls} data-testid="quote-name-input" />
              <input required placeholder="Téléphone *" value={form.phone} onChange={set("phone")} className={inputCls} data-testid="quote-phone-input" />
              <input type="email" placeholder="Email (optionnel)" value={form.email} onChange={set("email")} className={inputCls} data-testid="quote-email-input" />
              <select required value={form.service} onChange={set("service")} className={inputCls} data-testid="quote-service-select">
                <option value="" disabled>Type de besoin *</option>
                {CATEGORIES.map((c) => (
                  <option key={c.id} value={c.id}>{c.label}</option>
                ))}
                <option value="autre">Autre / multi-services</option>
              </select>
              <textarea
                rows={3}
                placeholder="Décrivez votre besoin : dates, lieu, quantités…"
                value={form.message}
                onChange={set("message")}
                className={`${inputCls} resize-none`}
                data-testid="quote-message-input"
              />
              <button
                type="submit"
                disabled={sending}
                data-testid="quote-submit-button"
                className="bg-[#F2541B] hover:bg-[#D9420B] disabled:opacity-60 text-white font-display font-bold uppercase tracking-wide px-6 py-4 rounded-full transition-colors duration-300"
              >
                {sending ? "Envoi en cours…" : "Envoyer ma demande"}
              </button>
            </form>

            <div className="mt-6 pt-5 border-t border-[#21262D] flex flex-wrap gap-4 justify-between items-center">
              <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" data-testid="quote-whatsapp-link" className="font-mono text-xs text-[#7BD8A4] hover:text-[#F0F6FC] transition-colors uppercase tracking-wider">
                Ou via WhatsApp →
              </a>
              <span className="font-mono text-xs text-[#8B949E]">{SITE.phoneDisplay}</span>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
